import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { generateClient } from 'aws-amplify/api';
import { Schema } from '../../../../../amplify/data/resource';
import TextField from '../../../../components/custom/TextField';
import Select from '../../../../components/custom/Select';
import Button from '../../../../components/custom/Button';
import DatePicker from '../../../../components/custom/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

const apiClient = generateClient<Schema>();

// @ts-ignore
type Coupon = Schema['Coupon']['type'];

interface EditCouponDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  coupon: Coupon | null;
}

interface CouponFormData {
  code: string;
  description: string;
  discountType: 'PERCENT' | 'AMOUNT';
  discountValue: string;
  startDate: Dayjs | null;
  expirationDate: Dayjs | null;
  usageLimit: string;
}

const EditCouponDialog: React.FC<EditCouponDialogProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  coupon 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CouponFormData>({
    defaultValues: {
      code: '',
      description: '',
      discountType: 'PERCENT',
      discountValue: '',
      startDate: null,
      expirationDate: null,
      usageLimit: '',
    },
  });

  // Устанавливаем значения формы при изменении coupon
  useEffect(() => {
    if (coupon && open) {
      reset({
        code: coupon.code || '',
        description: coupon.description || '',
        discountType: coupon.discountType || 'PERCENT',
        discountValue: coupon.discountValue?.toString() || '',
        startDate: coupon.startDate ? dayjs(coupon.startDate) : null,
        expirationDate: coupon.expirationDate ? dayjs(coupon.expirationDate) : null,
        usageLimit: coupon.usageLimit?.toString() || '',
      });
    }
  }, [coupon, open, reset]);

  const discountTypeOptions = [
    { label: 'אחוז', value: 'PERCENT' },
    { label: 'סכום קבוע', value: 'AMOUNT' },
  ];

  const handleFormSubmit = async (data: CouponFormData) => {
    if (!coupon?.id) {
      enqueueSnackbar('שגיאה: לא נמצא מזהה הקופון', { variant: 'error' });
      return;
    }

    try {
      const submitData = {
        id: coupon.id,
        code: data.code,
        description: data.description,
        discountType: data.discountType,
        discountValue: parseFloat(data.discountValue),
        startDate: data.startDate ? data.startDate.toISOString() : new Date().toISOString(),
        expirationDate: data.expirationDate ? data.expirationDate.toISOString() : new Date().toISOString(),
        usageLimit: data.usageLimit ? parseInt(data.usageLimit) : null,
        // Сохраняем существующие значения
        totalUses: coupon.totalUses || 0,
        status: coupon.status || 'ACTIVE',
      };

      // Обновляем купон в базе данных
      const result = await apiClient.models.Coupon.update(submitData);
      
      if (result.data) {
        console.log('Coupon updated successfully:', result.data);
        enqueueSnackbar('קופון עודכן בהצלחה!', { variant: 'success' });
        onSubmit(result.data); // Передаем обновленный объект родителю
        onClose(); // Закрываем диалог только при успехе
      } else {
        throw new Error('Failed to update coupon');
      }
    } catch (error) {
      console.error('Error updating coupon:', error);
      enqueueSnackbar('שגיאה בעדכון הקופון. אנא נסה שוב.', { variant: 'error' });
      // При ошибке диалог остается открытым и форма не сбрасывается
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle sx={{ direction: 'rtl' }}>
        <Typography
          fontSize="24px"
          fontWeight="700"
          fontFamily="AlmoniDL"
          textAlign="center"
        >
          ערוך קופון
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ direction: 'rtl', padding: '24px' }}>
        <Stack spacing={3}>
          <Controller
            name="code"
            control={control}
            rules={{ required: 'קוד קופון הוא שדה חובה' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="קוד קופון"
                error={!!errors.code}
                helperText={errors.code?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="תיאור (אופציונלי)"
                fullWidth
              />
            )}
          />

          <Controller
            name="discountType"
            control={control}
            rules={{ required: 'סוג הנחה הוא שדה חובה' }}
            render={({ field }) => (
              <Select
                {...field}
                label="סוג הנחה"
                options={discountTypeOptions}
                error={!!errors.discountType}
                helperText={errors.discountType?.message}
                id="discountType"
              />
            )}
          />

          <Controller
            name="discountValue"
            control={control}
            rules={{ 
              required: 'ערך הנחה הוא שדה חובה',
              pattern: {
                value: /^\d+(\.\d+)?$/,
                message: 'יש להזין מספר תקין'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="ערך הנחה"
                error={!!errors.discountValue}
                helperText={errors.discountValue?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="startDate"
            control={control}
            rules={{ required: 'תאריך התחלה הוא שדה חובה' }}
            render={({ field }) => (
              <DatePicker
                label="תאריך התחלה"
                value={field.value}
                onChange={field.onChange}
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="expirationDate"
            control={control}
            rules={{ required: 'תאריך סיום הוא שדה חובה' }}
            render={({ field }) => (
              <DatePicker
                label="תאריך סיום"
                value={field.value}
                onChange={field.onChange}
                error={!!errors.expirationDate}
                helperText={errors.expirationDate?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="usageLimit"
            control={control}
            rules={{
              pattern: {
                value: /^\d+$/,
                message: 'יש להזין מספר שלם'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="מגבלת שימוש (אופציונלי)"
                error={!!errors.usageLimit}
                helperText={errors.usageLimit?.message}
                fullWidth
                placeholder="השאר ריק ללא מגבלה"
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ direction: 'rtl', padding: '16px 24px', gap: '12px' }}>
        <Button
          onClick={handleClose}
          customVariant="light"
        >
          ביטול
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          customVariant="default"
          loading={isSubmitting}
        >
          עדכן קופון
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCouponDialog; 